import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AdminLayout from '../UI/Layout/AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLayerGroup, faPlus, faSyncAlt, faTrash, faPen, faArrowLeft, faTimes,
    faChevronLeft, faChevronRight, faExclamationTriangle, faBoxOpen,
    faFolderOpen, faFilter, faEye, faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Axios from '../../axios/Axios';
import { userloginDetails } from '../Utils/AdminAuth';

const BUCKET_API_URL = 'https://stagnapi.authkey.io/api/v1/admin/ActivityLogBucket';

const RESULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const selectStyles = {
    control: (provided) => ({ ...provided, cursor: 'pointer', minHeight: '38px', fontSize: '13px' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (style) => ({ ...style, fontSize: '13px' }),
};

const OPERATOR_LABELS = {
    eq: 'is',
    in: 'is one of',
    contains: 'contains',
    between: 'between',
};

const emptyCondition = () => ({ field: '', operator: '', value: null });

const formatDateInput = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const parseDateInput = (str) => {
    if (!str) return null;
    const parts = String(str).split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    const [y, m, d] = parts;
    return new Date(y, m - 1, d);
};

const startOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

// Same quick-range convention used on the Activity Log / User Journey pages,
// so picking a Created Date condition doesn't require manually clicking out
// two calendar dates for the common cases.
const DATE_PRESETS = [
    { key: 'today', label: 'Today', range: () => { const t = startOfDay(new Date()); return [t, t]; } },
    { key: 'yesterday', label: 'Yesterday', range: () => { const t = startOfDay(new Date()); const y = new Date(t); y.setDate(y.getDate() - 1); return [y, y]; } },
    { key: 'last7', label: 'Last 7 Days', range: () => { const t = startOfDay(new Date()); const s = new Date(t); s.setDate(s.getDate() - 6); return [s, t]; } },
    { key: 'last30', label: 'Last 30 Days', range: () => { const t = startOfDay(new Date()); const s = new Date(t); s.setDate(s.getDate() - 29); return [s, t]; } },
    { key: 'thismonth', label: 'This Month', range: () => { const t = startOfDay(new Date()); const s = new Date(t.getFullYear(), t.getMonth(), 1); return [s, t]; } },
];

const activeDatePresetKey = (value) => {
    if (!value || !value.start || !value.end) return null;
    const match = DATE_PRESETS.find((p) => {
        const [s, e] = p.range();
        return formatDateInput(s) === value.start && formatDateInput(e) === value.end;
    });
    return match ? match.key : null;
};

// Turns a saved condition back into a readable sentence for bucket cards / the
// detail header — driven entirely by the field's catalog label + operator, so a
// newly added field (e.g. country) reads correctly with no extra code here.
const describeCondition = (condition, fieldCatalog) => {
    const fieldDef = fieldCatalog.find((f) => f.field === condition.field);
    const label = fieldDef ? fieldDef.label : condition.field;
    const opLabel = OPERATOR_LABELS[condition.operator] || condition.operator;
    if (condition.operator === 'in' && Array.isArray(condition.value)) {
        return `${label} ${opLabel} ${condition.value.join(', ')}`;
    }
    if (condition.operator === 'between' && condition.value && typeof condition.value === 'object') {
        return `${label} ${opLabel} ${condition.value.start} and ${condition.value.end}`;
    }
    return `${label} ${opLabel} "${condition.value}"`;
};

// Renders one condition's field/operator/value inputs. Fetches its own "select"
// value options live (scoped by any other select-eq conditions already chosen,
// e.g. Module -> Event) rather than hardcoding module/event lists.
const ConditionRow = ({ condition, index, fieldCatalog, otherEqFilters, fetchFieldValues, onChange, onRemove, disableRemove, usedFieldsElsewhere, duplicateSelectFields }) => {
    const fieldDef = fieldCatalog.find((f) => f.field === condition.field);
    const [options, setOptions] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);

    const filterKey = JSON.stringify(otherEqFilters);

    useEffect(() => {
        if (!fieldDef || fieldDef.type !== 'select') {
            setOptions([]);
            return;
        }
        let cancelled = false;
        setOptionsLoading(true);
        fetchFieldValues(fieldDef.field, otherEqFilters)
            .then((opts) => { if (!cancelled) setOptions(opts); })
            .finally(() => { if (!cancelled) setOptionsLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldDef && fieldDef.field, filterKey]);

    const fieldOptions = fieldCatalog.map((f) => ({ value: f.field, label: f.label }));
    const operatorOptions = (fieldDef ? fieldDef.operators : []).map((op) => ({ value: op, label: OPERATOR_LABELS[op] || op }));
    const isDuplicateSelectField = fieldDef && fieldDef.type === 'select' && duplicateSelectFields.includes(fieldDef.field);

    return (
        <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start',
            background: '#fafbfd', border: isDuplicateSelectField ? '1px solid #fca5a5' : '1px solid #eef0f6', borderRadius: '10px',
            padding: '12px', marginBottom: '10px',
        }}>
            <div style={{ flex: '1 1 160px', minWidth: '160px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', display: 'block' }}>Field</label>
                <Select
                    value={fieldDef ? { value: fieldDef.field, label: fieldDef.label } : null}
                    onChange={(opt) => onChange(index, { field: opt.value, operator: '', value: null })}
                    options={fieldOptions}
                    // A "select" field (Module, Event, ...) already used by another row is
                    // disabled here rather than allowed to duplicate: two rows on the same
                    // select field AND together on save (e.g. module=A AND module=B), which
                    // can never match anything. Combining multiple values belongs in ONE
                    // row via the "is one of" operator's multi-select instead.
                    isOptionDisabled={(opt) => {
                        const optDef = fieldCatalog.find((f) => f.field === opt.value);
                        return !!optDef && optDef.type === 'select' && opt.value !== condition.field && usedFieldsElsewhere.includes(opt.value);
                    }}
                    styles={selectStyles}
                    placeholder="Select field..."
                    menuPortalTarget={document.body}
                />
                {isDuplicateSelectField && (
                    <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px' }}>
                        "{fieldDef.label}" is already used above — add its extra values to that row's "is one of" list instead of a new row.
                    </div>
                )}
            </div>

            <div style={{ flex: '1 1 140px', minWidth: '140px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', display: 'block' }}>Condition</label>
                <Select
                    value={condition.operator ? { value: condition.operator, label: OPERATOR_LABELS[condition.operator] || condition.operator } : null}
                    onChange={(opt) => onChange(index, { operator: opt.value, value: opt.value === 'in' ? [] : (opt.value === 'between' ? { start: null, end: null } : '') })}
                    options={operatorOptions}
                    styles={selectStyles}
                    placeholder="Select..."
                    isDisabled={!fieldDef}
                    menuPortalTarget={document.body}
                />
            </div>

            <div style={{ flex: '2 1 220px', minWidth: '220px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px', display: 'block' }}>Value</label>
                {!fieldDef || !condition.operator ? (
                    <div style={{ fontSize: '12px', color: '#94a3b8', padding: '9px 0' }}>Choose a field and condition first</div>
                ) : fieldDef.type === 'select' && condition.operator === 'in' ? (
                    <Select
                        isMulti
                        value={(condition.value || []).map((v) => ({ value: v, label: v }))}
                        onChange={(opts) => onChange(index, { value: (opts || []).map((o) => o.value) })}
                        options={options}
                        isLoading={optionsLoading}
                        styles={selectStyles}
                        placeholder="Select values..."
                        menuPortalTarget={document.body}
                    />
                ) : fieldDef.type === 'select' ? (
                    <Select
                        value={condition.value ? { value: condition.value, label: condition.value } : null}
                        onChange={(opt) => onChange(index, { value: opt ? opt.value : '' })}
                        options={options}
                        isLoading={optionsLoading}
                        styles={selectStyles}
                        placeholder="Select value..."
                        isClearable
                        menuPortalTarget={document.body}
                    />
                ) : fieldDef.type === 'text' ? (
                    <input
                        type="text"
                        className="form-control"
                        style={{ fontSize: '13px' }}
                        value={condition.value || ''}
                        onChange={(e) => onChange(index, { value: e.target.value })}
                        placeholder="Enter keyword..."
                    />
                ) : null}
            </div>

            <div style={{ flex: '0 0 auto', paddingTop: '22px' }}>
                <button
                    type="button"
                    onClick={() => onRemove(index)}
                    disabled={disableRemove}
                    title="Remove condition"
                    style={{
                        width: '34px', height: '34px', borderRadius: '8px', border: '1px solid #fecaca',
                        background: disableRemove ? '#f8fafc' : '#fef2f2', color: disableRemove ? '#cbd5e1' : '#dc2626',
                        cursor: disableRemove ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '12px' }} />
                </button>
            </div>
        </div>
    );
};

const isConditionComplete = (condition) => {
    if (!condition.field || !condition.operator) return false;
    if (condition.operator === 'in') return Array.isArray(condition.value) && condition.value.length > 0;
    if (condition.operator === 'between') return !!(condition.value && condition.value.start && condition.value.end);
    return typeof condition.value === 'string' && condition.value.trim() !== '';
};

// A daterange field (Created Date) gets its own always-visible, optional picker
// instead of living in the dynamic Field/Condition/Value list - so narrowing a
// bucket by date doesn't require first discovering "Add Condition" -> "Created
// Date". Leaving it blank simply omits that condition from the saved bucket.
const DateRangeFilter = ({ fieldDef, value, onChange }) => {
    const activeKey = activeDatePresetKey(value);
    const hasValue = !!(value.start || value.end);
    return (
        <div style={{ background: '#fafbfd', border: '1px solid #eef0f6', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '700', color: '#374151', margin: 0 }}>
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px', color: '#6b7280' }} />
                    {fieldDef.label}
                </label>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Optional — leave blank to include every date</span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                {DATE_PRESETS.map((preset) => {
                    const active = activeKey === preset.key;
                    return (
                        <button
                            key={preset.key}
                            type="button"
                            onClick={() => {
                                const [s, e] = preset.range();
                                onChange({ start: formatDateInput(s), end: formatDateInput(e) });
                            }}
                            style={{
                                height: '26px', padding: '0 10px', borderRadius: '14px',
                                fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                                border: '1px solid', borderColor: active ? '#4f46e5' : '#e2e8f0',
                                background: active ? '#4f46e5' : '#fff', color: active ? '#fff' : '#475569',
                            }}
                        >
                            {preset.label}
                        </button>
                    );
                })}
                {hasValue && (
                    <button
                        type="button"
                        onClick={() => onChange({ start: '', end: '' })}
                        style={{
                            height: '26px', padding: '0 10px', borderRadius: '14px',
                            fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                            border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626',
                        }}
                    >
                        Clear
                    </button>
                )}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
                <DatePicker
                    selected={parseDateInput(value.start)}
                    onChange={(d) => onChange({ start: formatDateInput(d) })}
                    selectsStart
                    startDate={parseDateInput(value.start)}
                    endDate={parseDateInput(value.end)}
                    maxDate={parseDateInput(value.end) || undefined}
                    dateFormat="dd MMM yyyy"
                    className="form-control"
                    placeholderText="Start date"
                    isClearable
                />
                <DatePicker
                    selected={parseDateInput(value.end)}
                    onChange={(d) => onChange({ end: formatDateInput(d) })}
                    selectsEnd
                    startDate={parseDateInput(value.start)}
                    endDate={parseDateInput(value.end)}
                    minDate={parseDateInput(value.start)}
                    dateFormat="dd MMM yyyy"
                    className="form-control"
                    placeholderText="End date"
                    isClearable
                />
            </div>
        </div>
    );
};

const BucketModal = ({ initialBucket, fieldCatalog, fetchFieldValues, onClose, onSaved, call }) => {
    const isEdit = !!initialBucket;
    const [name, setName] = useState(initialBucket?.name || '');
    const [description, setDescription] = useState(initialBucket?.description || '');

    // Daterange-type fields (Created Date) are pulled out of the dynamic condition
    // list and driven by their own DateRangeFilter section below instead.
    const daterangeFieldDefs = useMemo(() => fieldCatalog.filter((f) => f.type === 'daterange'), [fieldCatalog]);
    const dynamicFieldCatalog = useMemo(() => fieldCatalog.filter((f) => f.type !== 'daterange'), [fieldCatalog]);
    const daterangeFields = useMemo(() => daterangeFieldDefs.map((f) => f.field), [daterangeFieldDefs]);

    const [conditions, setConditions] = useState(() => {
        const nonDate = (initialBucket?.conditions || []).filter((c) => !daterangeFields.includes(c.field));
        return nonDate.length ? nonDate.map((c) => ({ ...c })) : [emptyCondition()];
    });
    const [dateFilters, setDateFilters] = useState(() => {
        const map = {};
        daterangeFieldDefs.forEach((f) => { map[f.field] = { start: '', end: '' }; });
        (initialBucket?.conditions || []).forEach((c) => {
            if (daterangeFields.includes(c.field) && c.value) {
                map[c.field] = { start: c.value.start || '', end: c.value.end || '' };
            }
        });
        return map;
    });
    const updateDateFilter = (field, patch) => {
        setDateFilters((prev) => ({ ...prev, [field]: { ...prev[field], ...patch } }));
    };
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const updateCondition = (index, patch) => {
        setConditions((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    };
    const removeCondition = (index) => {
        setConditions((prev) => prev.filter((_, i) => i !== index));
    };
    const addCondition = () => setConditions((prev) => [...prev, emptyCondition()]);

    // Select-type fields (Module, Event, ...) claimed by more than one condition row -
    // every condition ANDs together on save, so e.g. two rows both on Module can never
    // both be true for the same record and would silently zero out the bucket. Combining
    // multiple values belongs in ONE row via the "is one of" (in) operator instead.
    const duplicateSelectFields = useMemo(() => {
        const counts = {};
        conditions.forEach((c) => {
            const fieldDef = fieldCatalog.find((f) => f.field === c.field);
            if (c.field && fieldDef && fieldDef.type === 'select') {
                counts[c.field] = (counts[c.field] || 0) + 1;
            }
        });
        return Object.keys(counts).filter((f) => counts[f] > 1);
    }, [conditions, fieldCatalog]);

    const handleSave = async () => {
        setFormError('');
        if (!name.trim()) { setFormError('Bucket name is required.'); return; }

        // A row the user never touched (no field picked yet) is dropped silently rather
        // than blocking save - only rows where a field WAS picked must be complete.
        const activeConditions = conditions.filter((c) => c.field);
        if (!activeConditions.every(isConditionComplete)) {
            setFormError('Every condition needs a field, a condition type, and a value.');
            return;
        }
        if (duplicateSelectFields.length > 0) {
            const labels = duplicateSelectFields.map((f) => (fieldCatalog.find((fd) => fd.field === f) || {}).label || f);
            setFormError(`"${labels.join('", "')}" is used in more than one condition. Combine those values into a single "is one of" condition instead - separate rows on the same field can never all match at once.`);
            return;
        }

        const dateConditions = [];
        for (const fieldDef of daterangeFieldDefs) {
            const v = dateFilters[fieldDef.field] || {};
            if (!v.start && !v.end) continue;
            if (!v.start || !v.end) {
                setFormError(`Pick both a start and end date for "${fieldDef.label}", or clear both to skip it.`);
                return;
            }
            if (v.start > v.end) {
                setFormError(`"${fieldDef.label}" start date must not be after its end date.`);
                return;
            }
            dateConditions.push({ field: fieldDef.field, operator: 'between', value: { start: v.start, end: v.end } });
        }

        const finalConditions = [...activeConditions, ...dateConditions];
        if (finalConditions.length === 0) {
            setFormError('Add at least one condition or a date range.');
            return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await call('updateBucket', { id: initialBucket.id, name, description, conditions: finalConditions });
            } else {
                await call('createBucket', { name, description, conditions: finalConditions });
            }
            onSaved();
        } catch (err) {
            setFormError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1050,
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto',
        }}>
            <div style={{
                background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '760px',
                boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '18px 22px', borderBottom: '1px solid #eef0f6',
                }}>
                    <h5 style={{ margin: 0, fontWeight: '700', color: '#1a1a2e', fontSize: '16px' }}>
                        {isEdit ? 'Edit Bucket' : 'Create Bucket'}
                    </h5>
                    <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', color: '#94a3b8', fontSize: '16px', cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div style={{ padding: '22px', maxHeight: '70vh', overflowY: 'auto' }}>
                    {formError && (
                        <div className="alert alert-danger" style={{ fontSize: '13px' }}>{formError}</div>
                    )}

                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px', display: 'block' }}>Bucket Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. WhatsApp signups this month"
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px', display: 'block' }}>Description (optional)</label>
                        <textarea
                            className="form-control"
                            rows={2}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What is this bucket for?"
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '700', color: '#374151', margin: 0 }}>
                            <FontAwesomeIcon icon={faFilter} style={{ marginRight: '6px', color: '#6b7280' }} />
                            Conditions
                        </label>
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>Only rows matching ALL conditions land in this bucket</span>
                    </div>

                    {conditions.map((condition, index) => {
                        const otherEqFilters = conditions.reduce((acc, c, i) => {
                            if (i !== index && c.field && c.operator === 'eq' && typeof c.value === 'string' && c.value) {
                                acc[c.field] = c.value;
                            }
                            return acc;
                        }, {});
                        // Fields already claimed by OTHER rows - disables picking the same
                        // select field (Module, Event, ...) twice, since two rows on one
                        // field AND together into an impossible match (see duplicateSelectFields).
                        const usedFieldsElsewhere = conditions
                            .filter((c, i) => i !== index && c.field)
                            .map((c) => c.field);
                        return (
                            <ConditionRow
                                key={index}
                                index={index}
                                condition={condition}
                                fieldCatalog={dynamicFieldCatalog}
                                otherEqFilters={otherEqFilters}
                                usedFieldsElsewhere={usedFieldsElsewhere}
                                duplicateSelectFields={duplicateSelectFields}
                                fetchFieldValues={fetchFieldValues}
                                onChange={updateCondition}
                                onRemove={removeCondition}
                                disableRemove={false}
                            />
                        );
                    })}

                    <button
                        type="button"
                        onClick={addCondition}
                        style={{
                            border: '1px dashed #c7d2fe', background: '#eef2ff', color: '#4338ca',
                            borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                        }}
                    >
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />
                        Add Condition
                    </button>

                    {daterangeFieldDefs.map((fieldDef) => (
                        <div key={fieldDef.field} style={{ marginTop: '18px' }}>
                            <DateRangeFilter
                                fieldDef={fieldDef}
                                value={dateFilters[fieldDef.field] || { start: '', end: '' }}
                                onChange={(patch) => updateDateFilter(fieldDef.field, patch)}
                            />
                        </div>
                    ))}
                </div>

                <div style={{
                    display: 'flex', justifyContent: 'flex-end', gap: '10px',
                    padding: '16px 22px', borderTop: '1px solid #eef0f6',
                }}>
                    <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <span className="spinner-border spinner-border-sm" /> : (isEdit ? 'Save Changes' : 'Create Bucket')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ActivityLogBuckets = () => {
    const history = useHistory();
    const lStorage = userloginDetails();
    const adminToken = lStorage?.token;
    const userid = lStorage?.user?.userId;

    const [fieldCatalog, setFieldCatalog] = useState([]);
    const [buckets, setBuckets] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');

    const [modalState, setModalState] = useState({ open: false, bucket: null });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [activeBucketId, setActiveBucketId] = useState(null);
    const [detail, setDetail] = useState(null); // { bucket, results, total, page, limit }
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState('');
    const [pageSize, setPageSize] = useState(RESULT_PAGE_SIZE_OPTIONS[1]);
    const [expandedRow, setExpandedRow] = useState(null);

    const call = useCallback(async (method, params = {}) => {
        const { data } = await Axios.post(BUCKET_API_URL, {
            method, user_id: userid, token: adminToken, ...params,
        });
        if (data.success === false) throw new Error(data.message || 'Request failed');
        return data;
    }, [userid, adminToken]);

    const fetchFieldValues = useCallback(async (field, filters) => {
        try {
            const data = await call('getFieldValues', { field, filters });
            return (data.data || []).map((v) => ({ value: v, label: v }));
        } catch (err) {
            return [];
        }
    }, [call]);

    const loadBuckets = useCallback(async () => {
        setListLoading(true);
        setListError('');
        try {
            const data = await call('listBuckets');
            setBuckets(data.data || []);
        } catch (err) {
            setListError(err.message);
        } finally {
            setListLoading(false);
        }
    }, [call]);

    const loadCatalog = useCallback(async () => {
        try {
            const data = await call('getFieldCatalog');
            setFieldCatalog(data.data || []);
        } catch (err) {
            setListError(err.message);
        }
    }, [call]);

    useEffect(() => {
        loadCatalog();
        loadBuckets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openBucket = async (bucket, page = 1, limit = pageSize) => {
        setActiveBucketId(bucket.id);
        setDetailLoading(true);
        setDetailError('');
        setExpandedRow(null);
        try {
            const data = await call('getBucket', { id: bucket.id, page, limit });
            setDetail(data.data);
        } catch (err) {
            setDetailError(err.message);
        } finally {
            setDetailLoading(false);
        }
    };

    const refreshActiveBucket = () => {
        if (detail?.bucket) openBucket(detail.bucket, detail.page, detail.limit);
    };

    const backToList = () => {
        setActiveBucketId(null);
        setDetail(null);
        setDetailError('');
        loadBuckets();
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await call('deleteBucket', { id: deleteTarget.id });
            setDeleteTarget(null);
            if (activeBucketId === deleteTarget.id) backToList();
            else loadBuckets();
        } catch (err) {
            setListError(err.message);
            setDeleteTarget(null);
        } finally {
            setDeleting(false);
        }
    };

    const totalPages = detail ? Math.max(1, Math.ceil(detail.total / detail.limit)) : 1;

    const cardStyle = {
        background: '#fff', borderRadius: '12px', border: '1px solid #e8e8f0',
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)', padding: '18px 20px',
    };

    return (
        <AdminLayout>
            <div style={{ paddingBottom: '0.75rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {activeBucketId && (
                            <button
                                type="button"
                                onClick={backToList}
                                style={{
                                    border: '1px solid #e2e8f0', background: '#fff', color: '#334155',
                                    width: '36px', height: '36px', borderRadius: '10px', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                                title="Back to buckets"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </button>
                        )}
                        <span style={{
                            width: '40px', height: '40px', borderRadius: '11px', flexShrink: 0,
                            background: 'linear-gradient(135deg, #6366f1, #4338ca)', color: '#fff', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: '16px',
                            boxShadow: '0 4px 10px rgba(67,56,202,0.3)',
                        }}>
                            <FontAwesomeIcon icon={activeBucketId ? faFolderOpen : faLayerGroup} />
                        </span>
                        <div>
                            <h4 style={{ margin: 0, fontWeight: '700', color: '#1a1a2e', fontSize: '20px' }}>
                                {activeBucketId ? (detail?.bucket?.name || 'Bucket') : 'Activity Log Buckets'}
                            </h4>
                            <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: '13px' }}>
                                {activeBucketId
                                    ? 'Segmented activity log data — refreshed live every time it opens.'
                                    : 'Segment activity log data by module, event, message, or date into reusable buckets.'}
                            </p>
                        </div>
                    </div>

                    {!activeBucketId && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setModalState({ open: true, bucket: null })}
                            style={{ fontWeight: '600', fontSize: '13px' }}
                        >
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            Create Bucket
                        </button>
                    )}
                    {activeBucketId && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={refreshActiveBucket}
                            disabled={detailLoading}
                            style={{ fontWeight: '600', fontSize: '13px' }}
                        >
                            {detailLoading ? <span className="spinner-border spinner-border-sm" /> : (
                                <>
                                    <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
                                    Refresh
                                </>
                            )}
                        </button>
                    )}
                </div>

                {!activeBucketId && listError && (
                    <div className="alert alert-danger" role="alert">{listError}</div>
                )}

                {!activeBucketId ? (
                    <div style={{ ...cardStyle, padding: 0 }}>
                        {listLoading ? (
                            <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
                                <span className="spinner-border spinner-border-sm mr-2" /> Loading buckets...
                            </div>
                        ) : buckets.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                                <FontAwesomeIcon icon={faBoxOpen} style={{ fontSize: '28px', marginBottom: '12px' }} />
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#475569' }}>No buckets yet</div>
                                <div style={{ fontSize: '13px', marginTop: '4px' }}>Create a bucket to start segmenting activity log data.</div>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px', padding: '20px' }}>
                                {buckets.map((bucket) => (
                                    <div key={bucket.id} style={{
                                        border: '1px solid #eef0f6', borderRadius: '12px', padding: '16px',
                                        display: 'flex', flexDirection: 'column', gap: '10px', background: '#fafbfd',
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: '700', color: '#1a1a2e' }}>{bucket.name}</div>
                                                {bucket.description && (
                                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{bucket.description}</div>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                                <button type="button" title="Edit" onClick={() => setModalState({ open: true, bucket })}
                                                    style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer' }}>
                                                    <FontAwesomeIcon icon={faPen} style={{ fontSize: '11px' }} />
                                                </button>
                                                <button type="button" title="Delete" onClick={() => setDeleteTarget(bucket)}
                                                    style={{ width: '28px', height: '28px', borderRadius: '7px', border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}>
                                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: '11px' }} />
                                                </button>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {(bucket.conditions || []).map((c, i) => (
                                                <span key={i} style={{
                                                    fontSize: '11px', fontWeight: '600', color: '#4338ca', background: '#eef2ff',
                                                    border: '1px solid #c7d2fe', borderRadius: '20px', padding: '3px 10px',
                                                }}>
                                                    {describeCondition(c, fieldCatalog)}
                                                </span>
                                            ))}
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                Updated {bucket.updated_at ? new Date(bucket.updated_at).toLocaleString() : '—'}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => openBucket(bucket)}
                                                style={{
                                                    border: '1px solid #4f46e5', background: '#4f46e5', color: '#fff',
                                                    borderRadius: '8px', padding: '6px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEye} style={{ marginRight: '6px' }} />
                                                Open
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        {detailError && <div className="alert alert-danger" role="alert">{detailError}</div>}

                        {detail?.bucket && (
                            <div style={{ ...cardStyle, marginBottom: '16px' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: detail.bucket.description ? '8px' : 0 }}>
                                    {(detail.bucket.conditions || []).map((c, i) => (
                                        <span key={i} style={{
                                            fontSize: '11px', fontWeight: '600', color: '#4338ca', background: '#eef2ff',
                                            border: '1px solid #c7d2fe', borderRadius: '20px', padding: '3px 10px',
                                        }}>
                                            {describeCondition(c, fieldCatalog)}
                                        </span>
                                    ))}
                                </div>
                                {detail.bucket.description && (
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>{detail.bucket.description}</div>
                                )}
                            </div>
                        )}

                        <div style={{ ...cardStyle, padding: 0 }}>
                            <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '14px 20px', borderBottom: '1px solid #eef0f6', flexWrap: 'wrap', gap: '10px',
                            }}>
                                <div style={{ fontSize: '13px', color: '#334155', fontWeight: '600' }}>
                                    {detail ? `${detail.total.toLocaleString()} matching record${detail.total === 1 ? '' : 's'}` : '—'}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Page size</label>
                                    <select
                                        className="form-control form-control-sm"
                                        style={{ width: '90px', fontSize: '12px' }}
                                        value={pageSize}
                                        onChange={(e) => { const l = Number(e.target.value); setPageSize(l); if (detail?.bucket) openBucket(detail.bucket, 1, l); }}
                                    >
                                        {RESULT_PAGE_SIZE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                                    </select>
                                </div>
                            </div>

                            {detailLoading ? (
                                <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
                                    <span className="spinner-border spinner-border-sm mr-2" /> Loading bucket data...
                                </div>
                            ) : !detail || detail.results.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '6px' }} />
                                    No activity log rows currently match this bucket's conditions.
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="table" style={{ marginBottom: 0, fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ background: '#fafbfd' }}>
                                                <th style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Module</th>
                                                <th style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Event</th>
                                                <th style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Message</th>
                                                <th style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Created At</th>
                                                <th style={{ width: '40px' }} />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detail.results.map((row, idx) => (
                                                <React.Fragment key={row._id || idx}>
                                                    <tr>
                                                        <td>{row.module || '—'}</td>
                                                        <td>{row.event || '—'}</td>
                                                        <td style={{ maxWidth: '360px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {typeof row.message === 'string' ? row.message : JSON.stringify(row.message)}
                                                        </td>
                                                        <td style={{ whiteSpace: 'nowrap', color: '#64748b' }}>{row.created_at || '—'}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
                                                                style={{ border: 'none', background: 'transparent', color: '#4f46e5', cursor: 'pointer' }}
                                                                title="View raw row"
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {expandedRow === idx && (
                                                        <tr>
                                                            <td colSpan={5} style={{ background: '#f8fafc' }}>
                                                                <pre style={{ margin: 0, fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                                                    {JSON.stringify(row, null, 2)}
                                                                </pre>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {detail && detail.results.length > 0 && (
                                <div style={{
                                    display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px',
                                    padding: '12px 20px', borderTop: '1px solid #eef0f6',
                                }}>
                                    <span style={{ fontSize: '12px', color: '#64748b' }}>Page {detail.page} of {totalPages}</span>
                                    <button
                                        type="button"
                                        disabled={detail.page <= 1 || detailLoading}
                                        onClick={() => openBucket(detail.bucket, detail.page - 1, detail.limit)}
                                        style={{
                                            width: '30px', height: '30px', borderRadius: '7px', border: '1px solid #e2e8f0',
                                            background: '#fff', color: '#334155', cursor: detail.page <= 1 ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '11px' }} />
                                    </button>
                                    <button
                                        type="button"
                                        disabled={detail.page >= totalPages || detailLoading}
                                        onClick={() => openBucket(detail.bucket, detail.page + 1, detail.limit)}
                                        style={{
                                            width: '30px', height: '30px', borderRadius: '7px', border: '1px solid #e2e8f0',
                                            background: '#fff', color: '#334155', cursor: detail.page >= totalPages ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '11px' }} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {modalState.open && (
                <BucketModal
                    initialBucket={modalState.bucket}
                    fieldCatalog={fieldCatalog}
                    fetchFieldValues={fetchFieldValues}
                    call={call}
                    onClose={() => setModalState({ open: false, bucket: null })}
                    onSaved={() => {
                        setModalState({ open: false, bucket: null });
                        loadBuckets();
                        if (activeBucketId && modalState.bucket?.id === activeBucketId) {
                            openBucket({ id: activeBucketId }, detail?.page, detail?.limit);
                        }
                    }}
                />
            )}

            {deleteTarget && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1060,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
                }}>
                    <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '400px', padding: '22px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
                        <h6 style={{ fontWeight: '700', color: '#1a1a2e' }}>Delete "{deleteTarget.name}"?</h6>
                        <p style={{ fontSize: '13px', color: '#64748b' }}>
                            This removes the bucket definition only — the underlying activity log data is never affected.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                            <button type="button" className="btn btn-outline-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
                            <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                                {deleting ? <span className="spinner-border spinner-border-sm" /> : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default ActivityLogBuckets;
